import { t } from '../i18n/translations';
import { Role } from '../types';

export class ControlBar {
  private isAudioMuted = false;
  private isVideoMuted = false;
  private isScreenSharing = false;
  private isHandRaised = false;
  private isRecording = false;
  private showReactions = false;
  private participantCount = 1;
  private unreadChatCount = 0;
  private role: Role = 'participant';
  private isCaptionsActive = false;

  public onToggleAudio?: (muted: boolean) => void;
  public onToggleVideo?: (muted: boolean) => void;
  public onToggleScreenShare?: () => void;
  public onToggleRecord?: () => void;
  public onSendReaction?: (emoji: string) => void;
  public onToggleHandRaise?: (raised: boolean) => void;
  public onToggleWhiteboard?: () => void;
  public onToggleParticipants?: () => void;
  public onToggleChat?: () => void;
  public onTogglePolls?: () => void;
  public onToggleForms?: () => void;
  public onToggleCaptions?: (active: boolean) => void;
  public onToggleNotes?: () => void;
  public onOpenSettings?: () => void;
  public onLeave?: () => void;
  public onEndMeeting?: () => void;

  public updateState(opts: {
    audioMuted?: boolean;
    videoMuted?: boolean;
    screenSharing?: boolean;
    handRaised?: boolean;
    recording?: boolean;
    participantCount?: number;
    unreadChatCount?: number;
    captionsActive?: boolean;
    role?: Role;
  }) {
    if (opts.audioMuted !== undefined) this.isAudioMuted = opts.audioMuted;
    if (opts.videoMuted !== undefined) this.isVideoMuted = opts.videoMuted;
    if (opts.screenSharing !== undefined) this.isScreenSharing = opts.screenSharing;
    if (opts.handRaised !== undefined) this.isHandRaised = opts.handRaised;
    if (opts.recording !== undefined) this.isRecording = opts.recording;
    if (opts.captionsActive !== undefined) this.isCaptionsActive = opts.captionsActive;
    if (opts.participantCount !== undefined) this.participantCount = opts.participantCount;
    if (opts.unreadChatCount !== undefined) this.unreadChatCount = opts.unreadChatCount;
    if (opts.role !== undefined) this.role = opts.role;
  }

  public render(container: HTMLElement) {
    container.innerHTML = `
      <div class="meeting-controls-bar">
        <!-- Audio Mute / Unmute -->
        <button id="ctrl-mic" class="control-btn ${this.isAudioMuted ? 'active-danger' : ''}" title="${this.isAudioMuted ? t('unmute') : t('mute')}">
          ${this.isAudioMuted ? `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></line>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          ` : `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          `}
        </button>

        <!-- Camera On / Off -->
        <button id="ctrl-cam" class="control-btn ${this.isVideoMuted ? 'active-danger' : ''}" title="${this.isVideoMuted ? t('videoOn') : t('videoOff')}">
          ${this.isVideoMuted ? `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M21 21l-3.34-3.34M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          ` : `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          `}
        </button>

        <!-- Screen Share -->
        <button id="ctrl-screenshare" class="control-btn ${this.isScreenSharing ? 'active-brand' : ''}" title="${this.isScreenSharing ? t('stopSharing') : t('shareScreen')}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        </button>

        <!-- Meeting Recording -->
        <button id="ctrl-record" class="control-btn ${this.isRecording ? 'active-danger recording-pulse' : ''}" title="${this.isRecording ? 'Stop Recording' : 'Record Meeting'}">
          ${this.isRecording ? `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2"></rect>
            </svg>
          ` : `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="9"></circle>
              <circle cx="12" cy="12" r="4" fill="currentColor"></circle>
            </svg>
          `}
        </button>

        <!-- Quick Reactions & Hand Raise -->
        <div style="position: relative;">
          <button id="ctrl-reaction" class="control-btn ${this.isHandRaised ? 'active-brand' : ''}" title="Reactions & Hand Raise">
            <span style="font-size: 1.25rem;">${this.isHandRaised ? '✋' : '😊'}</span>
          </button>

          ${this.showReactions ? `
            <div class="reaction-picker">
              <button class="reaction-btn" data-emoji="👍">👍</button>
              <button class="reaction-btn" data-emoji="❤️">❤️</button>
              <button class="reaction-btn" data-emoji="😂">😂</button>
              <button class="reaction-btn" data-emoji="👏">👏</button>
              <button class="reaction-btn" data-emoji="🎉">🎉</button>
              <button class="reaction-btn" data-emoji="😮">😮</button>
              <button class="reaction-btn" data-emoji="✋" title="${this.isHandRaised ? t('lowerHand') : t('raiseHand')}">✋</button>
            </div>
          ` : ''}
        </div>

        <div style="height: 24px; width: 1px; background: var(--border-subtle); margin: 0 4px;"></div>

        <!-- Participants Toggle -->
        <button id="ctrl-participants" class="control-btn" title="${t('participants')}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          <span style="position: absolute; top: -4px; right: -4px; background: var(--brand-primary); color: white; font-size: 0.7rem; font-weight: 700; border-radius: var(--radius-full); width: 18px; height: 18px; display: flex; align-items: center; justify-content: center;">
            ${this.participantCount}
          </span>
        </button>

        <!-- Chat Toggle -->
        <button id="ctrl-chat" class="control-btn" title="${t('chat')}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          ${this.unreadChatCount > 0 ? `
            <span style="position: absolute; top: -4px; right: -4px; background: var(--color-danger); color: white; font-size: 0.7rem; font-weight: 700; border-radius: var(--radius-full); width: 18px; height: 18px; display: flex; align-items: center; justify-content: center;">
              ${this.unreadChatCount}
            </span>
          ` : ''}
        </button>

        <!-- Whiteboard Toggle -->
        <button id="ctrl-whiteboard" class="control-btn" title="Collaborative Whiteboard">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
            <path d="M2 2l7.586 7.586"></path>
            <circle cx="11" cy="11" r="2"></circle>
          </svg>
        </button>

        <!-- Polls Toggle -->
        <button id="ctrl-polls" class="control-btn" title="${t('polls')}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
          </svg>
        </button>

        <!-- Roll Call / Forms Toggle -->
        <button id="ctrl-forms" class="control-btn" title="Roll Call & Forms">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
            <path d="M9 12h6"></path>
            <path d="M9 16h6"></path>
          </svg>
        </button>

        <!-- Live Closed Captions (CC) -->
        <button id="ctrl-captions" class="control-btn ${this.isCaptionsActive ? 'active-brand' : ''}" title="${this.isCaptionsActive ? 'Turn off Live Captions' : 'Turn on Live Captions (CC)'}">
          <span style="font-weight: 800; font-size: 0.82rem; letter-spacing: -0.5px; border: 1.5px solid currentColor; border-radius: 4px; padding: 1px 3px; line-height: 1;">CC</span>
        </button>

        <!-- Shared Notes -->
        <button id="ctrl-notes" class="control-btn" title="Shared Collaborative Notes">
          <span style="font-size: 1.15rem;">📝</span>
        </button>

        <!-- Settings -->
        <button id="ctrl-settings" class="control-btn" title="${t('settings')}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <!-- Leave / End Call Buttons -->
        ${this.role === 'host' ? `
          <button id="ctrl-leave" class="control-btn" style="background: rgba(239, 68, 68, 0.12); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.25);" title="Leave Meeting (Keep Room Active)">
            <span>Leave</span>
          </button>
          <button id="ctrl-end-all" class="control-btn btn-leave" style="background: var(--color-danger); color: white;" title="End Meeting for All (Immediately delete room link)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.8 19.8 0 0 1-3.12-8.69A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path>
              <line x1="23" y1="1" x2="1" y2="23"></line>
            </svg>
            <span>End for All</span>
          </button>
        ` : `
          <button id="ctrl-leave" class="control-btn btn-leave" title="${t('leave')}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.8 19.8 0 0 1-3.12-8.69A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"></path>
              <line x1="23" y1="1" x2="1" y2="23"></line>
            </svg>
            <span>${t('leave')}</span>
          </button>
        `}
      </div>
    `;

    this.bindEvents(container);
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#ctrl-mic')?.addEventListener('click', () => {
      this.isAudioMuted = !this.isAudioMuted;
      this.render(container);
      if (this.onToggleAudio) this.onToggleAudio(this.isAudioMuted);
    });

    container.querySelector('#ctrl-cam')?.addEventListener('click', () => {
      this.isVideoMuted = !this.isVideoMuted;
      this.render(container);
      if (this.onToggleVideo) this.onToggleVideo(this.isVideoMuted);
    });

    container.querySelectorAll('#ctrl-screenshare, #ctrl-screen').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.onToggleScreenShare) this.onToggleScreenShare();
      });
    });

    container.querySelector('#ctrl-record')?.addEventListener('click', () => {
      if (this.onToggleRecord) this.onToggleRecord();
    });

    container.querySelectorAll('#ctrl-reaction, #ctrl-reactions').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showReactions = !this.showReactions;
        this.render(container);
      });
    });

    container.querySelectorAll('.reaction-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji');
        if (emoji === '✋') {
          this.isHandRaised = !this.isHandRaised;
          if (this.onToggleHandRaise) this.onToggleHandRaise(this.isHandRaised);
        } else if (emoji && this.onSendReaction) {
          this.onSendReaction(emoji);
        }
        this.showReactions = false;
        this.render(container);
      });
    });

    container.querySelector('#ctrl-participants')?.addEventListener('click', () => {
      if (this.onToggleParticipants) this.onToggleParticipants();
    });

    container.querySelector('#ctrl-chat')?.addEventListener('click', () => {
      this.unreadChatCount = 0;
      this.render(container);
      if (this.onToggleChat) this.onToggleChat();
    });

    container.querySelector('#ctrl-whiteboard')?.addEventListener('click', () => {
      if (this.onToggleWhiteboard) this.onToggleWhiteboard();
    });

    container.querySelector('#ctrl-polls')?.addEventListener('click', () => {
      if (this.onTogglePolls) this.onTogglePolls();
    });

    container.querySelector('#ctrl-forms')?.addEventListener('click', () => {
      if (this.onToggleForms) this.onToggleForms();
    });

    container.querySelector('#ctrl-captions')?.addEventListener('click', () => {
      this.isCaptionsActive = !this.isCaptionsActive;
      this.render(container);
      if (this.onToggleCaptions) this.onToggleCaptions(this.isCaptionsActive);
    });

    container.querySelector('#ctrl-notes')?.addEventListener('click', () => {
      if (this.onToggleNotes) this.onToggleNotes();
    });

    container.querySelector('#ctrl-settings')?.addEventListener('click', () => {
      if (this.onOpenSettings) this.onOpenSettings();
    });

    container.querySelector('#ctrl-end-all')?.addEventListener('click', () => {
      const choice = confirm('End this meeting for all participants? All participants will be disconnected immediately and the meeting link will be deleted.');
      if (choice && this.onEndMeeting) {
        this.onEndMeeting();
      }
    });

    container.querySelector('#ctrl-leave')?.addEventListener('click', () => {
      if (this.onLeave) this.onLeave();
    });

    // Close reaction popup on outside click
    document.addEventListener('click', () => {
      if (this.showReactions) {
        this.showReactions = false;
        this.render(container);
      }
    });
  }
}
